import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { LocalStorageService } from './storage.service';

@Injectable({
  providedIn: 'root',
})
export class SessionTimeoutService implements OnDestroy {
  private readonly SESSION_TIMEOUT_MS = 2 * 60 * 60 * 1000; // 2 hours in milliseconds
  private readonly WARNING_TIME_MS = 5 * 60 * 1000; // 5 minutes before timeout
  private readonly ACTIVITY_CHECK_INTERVAL_MS = 60 * 1000; // Check every minute

  private lastActivityTime: number = Date.now();
  private timeoutTimer: any;
  private warningTimer: any;
  private activityCheckTimer: any;
  private isSessionActive = new BehaviorSubject<boolean>(true);
  private timeUntilTimeout = new BehaviorSubject<number>(this.SESSION_TIMEOUT_MS);

  // Track if user is currently active (to avoid unnecessary warnings)
  private isUserActive = false;
  // Track if the page is currently visible/focused
  private isPageVisible = true;
  // Track when the page lost focus (for calculating time away)
  private pageBlurTime: number | null = null;
  // Track last user interaction (mouse/keyboard) time
  private lastUserInteractionTime: number = Date.now();
  // Callback function for logout (to avoid circular dependency)
  private logoutCallback: (() => void) | null = null;

  constructor(
    private localStorageService: LocalStorageService,
    private router: Router
  ) {
    this.initializeSessionTimeout();
    this.setupActivityListeners();
    this.startActivityCheckTimer();
  }

  ngOnDestroy(): void {
    this.clearAllTimers();
    this.removeActivityListeners();
  }

  /**
   * Initialize session timeout tracking
   */
  private initializeSessionTimeout(): void {
    // Check if there's a stored last activity time
    const storedLastActivity = sessionStorage.getItem('lastActivityTime');
    if (storedLastActivity) {
      this.lastActivityTime = parseInt(storedLastActivity, 10);
    }

    // Initialize page visibility state
    this.isPageVisible = !document.hidden;
    
    // Initialize last user interaction time
    this.lastUserInteractionTime = this.lastActivityTime;

    this.updateTimers();
  }

  /**
   * Set up event listeners to track user activity
   */
  private setupActivityListeners(): void {
    // Events that count as user interactions (mouse/keyboard)
    const userInteractionEvents = [
      'mousedown',
      'mousemove',
      'keypress',
      'keydown',
      'touchstart',
      'click'
    ];

    // Events that count as general activity (but not user interaction)
    const generalActivityEvents = [
      'scroll',
      'focus',
      'blur'
    ];

    // Track user interactions separately
    userInteractionEvents.forEach(event => {
      document.addEventListener(event, this.onUserInteraction.bind(this), true);
    });

    // Track general activity
    generalActivityEvents.forEach(event => {
      document.addEventListener(event, this.onUserActivity.bind(this), true);
    });

    // Track window focus/blur with special handling
    window.addEventListener('focus', this.onWindowFocus.bind(this));
    window.addEventListener('blur', this.onWindowBlur.bind(this));
    
    // Track page visibility changes (when user switches tabs)
    document.addEventListener('visibilitychange', this.onVisibilityChange.bind(this));
  }

  /**
   * Remove activity event listeners
   */
  private removeActivityListeners(): void {
    // Events that count as user interactions (mouse/keyboard)
    const userInteractionEvents = [
      'mousedown',
      'mousemove',
      'keypress',
      'keydown',
      'touchstart',
      'click'
    ];

    // Events that count as general activity (but not user interaction)
    const generalActivityEvents = [
      'scroll',
      'focus',
      'blur'
    ];

    // Remove user interaction listeners
    userInteractionEvents.forEach(event => {
      document.removeEventListener(event, this.onUserInteraction.bind(this), true);
    });

    // Remove general activity listeners
    generalActivityEvents.forEach(event => {
      document.removeEventListener(event, this.onUserActivity.bind(this), true);
    });

    window.removeEventListener('focus', this.onWindowFocus.bind(this));
    window.removeEventListener('blur', this.onWindowBlur.bind(this));
    document.removeEventListener('visibilitychange', this.onVisibilityChange.bind(this));
  }

  /**
   * Handle user interaction events (mouse/keyboard)
   */
  private onUserInteraction(): void {
    // Only track interaction if the page is visible
    if (this.isPageVisible) {
      this.lastActivityTime = Date.now();
      this.lastUserInteractionTime = Date.now();
      this.isUserActive = true;
      
      // Store the last activity time
      sessionStorage.setItem('lastActivityTime', this.lastActivityTime.toString());
      
      // Reset timers
      this.updateTimers();
    }
  }

  /**
   * Handle general user activity events (scroll, focus, etc.)
   */
  private onUserActivity(): void {
    // Only track activity if the page is visible
    if (this.isPageVisible) {
      this.lastActivityTime = Date.now();
      this.isUserActive = true;
      
      // Store the last activity time
      sessionStorage.setItem('lastActivityTime', this.lastActivityTime.toString());
      
      // Reset timers
      this.updateTimers();
    }
  }

  /**
   * Handle window focus event
   */
  private onWindowFocus(): void {
    this.isPageVisible = true;
    this.pageBlurTime = null;
    
    // Reset activity time when user returns to the page
    this.lastActivityTime = Date.now();
    this.lastUserInteractionTime = Date.now();
    this.isUserActive = true;
    
    // Store the last activity time
    sessionStorage.setItem('lastActivityTime', this.lastActivityTime.toString());
    
    // Reset timers
    this.updateTimers();
    
    //console.log('Page focused - session timeout reset');
  }

  /**
   * Handle window blur event
   */
  private onWindowBlur(): void {
    this.isPageVisible = false;
    this.pageBlurTime = Date.now();
    
    //console.log('Page blurred - session timeout paused');
  }

  /**
   * Handle page visibility change (tab switching)
   */
  private onVisibilityChange(): void {
    if (document.hidden) {
      // Page is hidden (user switched tabs or minimized window)
      this.isPageVisible = false;
      this.pageBlurTime = Date.now();
      //console.log('Page hidden - session timeout paused');
    } else {
      // Page is visible again
      this.isPageVisible = true;
      this.pageBlurTime = null;
      
      // Reset activity time when user returns to the page
      this.lastActivityTime = Date.now();
      this.lastUserInteractionTime = Date.now();
      this.isUserActive = true;
      
      // Store the last activity time
      sessionStorage.setItem('lastActivityTime', this.lastActivityTime.toString());
      
      // Reset timers
      this.updateTimers();
      
      //console.log('Page visible - session timeout reset');
    }
  }

  /**
   * Start periodic activity check timer
   */
  private startActivityCheckTimer(): void {
    this.activityCheckTimer = setInterval(() => {
      this.checkSessionTimeout();
    }, this.ACTIVITY_CHECK_INTERVAL_MS);
  }

  /**
   * Check if session should timeout
   */
  private checkSessionTimeout(): void {
    const now = Date.now();
    let timeSinceLastActivity = now - this.lastActivityTime;
    let timeSinceLastUserInteraction = now - this.lastUserInteractionTime;
    
    // If page is not visible, don't count the time away from the page
    if (!this.isPageVisible && this.pageBlurTime) {
      timeSinceLastActivity = this.pageBlurTime - this.lastActivityTime;
      timeSinceLastUserInteraction = this.pageBlurTime - this.lastUserInteractionTime;
    }
    
    const timeUntilTimeout = this.SESSION_TIMEOUT_MS - timeSinceLastActivity;

    this.timeUntilTimeout.next(Math.max(0, timeUntilTimeout));

    // Check for timeout conditions:
    // Page is visible and user has been inactive for the full timeout period
    if (this.isPageVisible) {
      if (timeSinceLastActivity >= this.SESSION_TIMEOUT_MS) {
        // Full timeout period has passed
        this.handleSessionTimeout();
      } else if (timeSinceLastActivity >= (this.SESSION_TIMEOUT_MS - this.WARNING_TIME_MS)) {
        // Show warning before full timeout
        this.handleSessionWarning();
      }
    }
  }

  /**
   * Update timeout and warning timers
   */
  private updateTimers(): void {
    this.clearTimers();

    const now = Date.now();
    let timeSinceLastActivity = now - this.lastActivityTime;
    let timeSinceLastUserInteraction = now - this.lastUserInteractionTime;
    
    // If page is not visible, don't count the time away from the page
    if (!this.isPageVisible && this.pageBlurTime) {
      timeSinceLastActivity = this.pageBlurTime - this.lastActivityTime;
      timeSinceLastUserInteraction = this.pageBlurTime - this.lastUserInteractionTime;
    }
    
    const timeUntilTimeout = this.SESSION_TIMEOUT_MS - timeSinceLastActivity;
    const timeUntilWarning = (this.SESSION_TIMEOUT_MS - this.WARNING_TIME_MS) - timeSinceLastActivity;

    // Only set timers if the page is visible
    if (this.isPageVisible) {
      // Set warning timer
      if (timeUntilWarning > 0) {
        this.warningTimer = setTimeout(() => {
          this.handleSessionWarning();
        }, timeUntilWarning);
      }

      // Set timeout timer
      if (timeUntilTimeout > 0) {
        this.timeoutTimer = setTimeout(() => {
          this.handleSessionTimeout();
        }, timeUntilTimeout);
      } else {
        // Session already expired
        this.handleSessionTimeout();
      }
    }
  }

  /**
   * Handle session warning (2 minutes before timeout)
   */
  private handleSessionWarning(): void {
    if (!this.isUserActive) {
      return; // Don't show warning if user is not active
    }

    // Calculate exact minutes remaining
    const remainingTime = this.getRemainingTime();
    const minutesLeft = Math.ceil(remainingTime / (60 * 1000));
    
    // You can emit an event or show a modal here
    //console.warn(`Session will expire in ${minutesLeft} minutes due to inactivity`);
    
    // Optionally show a warning modal or notification
    // this.showSessionWarningModal();
  }

  /**
   * Handle session timeout
   */
  private handleSessionTimeout(): void {
    //console.log('Session timed out due to inactivity');
    this.isSessionActive.next(false);
    this.clearAllTimers();
    this.clearAllStorage();
    
    // Use callback if available, otherwise navigate to login
    if (this.logoutCallback) {
      this.logoutCallback();
    } else {
      // Fallback: direct navigation to login
      this.router.navigate(['/login']).catch((error) => {
        //console.error('Navigation to login failed:', error);
        // Force page reload to login
        window.location.href = '/login';
      });
    }
  }

  /**
   * Force logout and redirect to login (for session timeout)
   */
  public forceLogoutAndRedirect(): void {
    //console.log('Force logout due to session timeout');
    this.isSessionActive.next(false);
    this.clearAllTimers();
    this.clearAllStorage();
    
    // Clear any remaining session data
    sessionStorage.removeItem('user');
    localStorage.clear();
    
    // Force redirect to login page
    window.location.href = '/login';
  }

  /**
   * Clear all storage (localStorage, sessionStorage, IndexedDB, cache)
   */
  private clearAllStorage(): void {
    try {
      // Clear localStorage
      localStorage.clear();
      
      // Clear sessionStorage (except for some system items if needed)
      const keysToPreserve = ['language', 'theme']; // Add any keys you want to preserve
      const preservedData: { [key: string]: string } = {};
      
      keysToPreserve.forEach(key => {
        const value = sessionStorage.getItem(key);
        if (value) {
          preservedData[key] = value;
        }
      });
      
      sessionStorage.clear();
      
      // Restore preserved data
      Object.keys(preservedData).forEach(key => {
        sessionStorage.setItem(key, preservedData[key]);
      });

      // Clear IndexedDB through LocalStorageService
      this.localStorageService.clearData();

      // Clear any cached data in memory
      this.clearMemoryCache();

      // Clear any service worker caches if they exist
      this.clearServiceWorkerCaches();

      //console.log('All storage cleared successfully');
    } catch (error) {
      //console.error('Error clearing storage:', error);
    }
  }

  /**
   * Clear memory cache
   */
  private clearMemoryCache(): void {
    // Clear any global variables or cached data
    // This is application-specific and may need adjustment
    if (typeof window !== 'undefined') {
      // Clear any global cache objects
      (window as any).__CACHE__ = {};
    }
  }

  /**
   * Clear service worker caches
   */
  private async clearServiceWorkerCaches(): Promise<void> {
    try {
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(
          cacheNames.map(cacheName => caches.delete(cacheName))
        );
        //console.log('Service worker caches cleared');
      }
    } catch (error) {
      //console.error('Error clearing service worker caches:', error);
    }
  }

  /**
   * Clear timeout and warning timers
   */
  private clearTimers(): void {
    if (this.timeoutTimer) {
      clearTimeout(this.timeoutTimer);
      this.timeoutTimer = null;
    }
    if (this.warningTimer) {
      clearTimeout(this.warningTimer);
      this.warningTimer = null;
    }
  }

  /**
   * Clear all timers
   */
  private clearAllTimers(): void {
    this.clearTimers();
    if (this.activityCheckTimer) {
      clearInterval(this.activityCheckTimer);
      this.activityCheckTimer = null;
    }
  }

  /**
   * Set the logout callback function (to avoid circular dependency)
   */
  public setLogoutCallback(callback: () => void): void {
    this.logoutCallback = callback;
  }

  /**
   * Reset session timeout (call this when user performs important actions)
   */
  public resetSessionTimeout(): void {
    this.onUserInteraction();
  }

  /**
   * Get observable for session active state
   */
  public getSessionActiveObservable(): Observable<boolean> {
    return this.isSessionActive.asObservable();
  }

  /**
   * Get observable for time until timeout
   */
  public getTimeUntilTimeoutObservable(): Observable<number> {
    return this.timeUntilTimeout.asObservable();
  }

  /**
   * Get remaining time until timeout in milliseconds
   */
  public getRemainingTime(): number {
    const now = Date.now();
    let timeSinceLastActivity = now - this.lastActivityTime;
    let timeSinceLastUserInteraction = now - this.lastUserInteractionTime;
    
    // If page is not visible, don't count the time away from the page
    if (!this.isPageVisible && this.pageBlurTime) {
      timeSinceLastActivity = this.pageBlurTime - this.lastActivityTime;
      timeSinceLastUserInteraction = this.pageBlurTime - this.lastUserInteractionTime;
    }
    
    const timeUntilActivityTimeout = this.SESSION_TIMEOUT_MS - timeSinceLastActivity;
    
    // Return the time until activity timeout
    return Math.max(0, timeUntilActivityTimeout);
  }

  /**
   * Get remaining time until timeout in minutes
   */
  public getRemainingTimeInMinutes(): number {
    return Math.ceil(this.getRemainingTime() / (60 * 1000));
  }

  /**
   * Get warning time in minutes (when to show warnings)
   */
  public getWarningTimeInMinutes(): number {
    return Math.ceil(this.WARNING_TIME_MS / (60 * 1000));
  }

  /**
   * Get time since last user interaction in minutes
   */
  public getTimeSinceLastUserInteractionInMinutes(): number {
    const now = Date.now();
    let timeSinceLastUserInteraction = now - this.lastUserInteractionTime;
    
    // If page is not visible, don't count the time away from the page
    if (!this.isPageVisible && this.pageBlurTime) {
      timeSinceLastUserInteraction = this.pageBlurTime - this.lastUserInteractionTime;
    }
    
    return Math.floor(timeSinceLastUserInteraction / (60 * 1000));
  }

  /**
   * Check if session is currently active
   */
  public isSessionCurrentlyActive(): boolean {
    return this.isSessionActive.value;
  }

  /**
   * Manually trigger session timeout (for testing or admin purposes)
   */
  public forceSessionTimeout(): void {
    this.handleSessionTimeout();
  }

  /**
   * Test session timeout with immediate logout (for testing)
   */
  public testSessionTimeout(): void {
    //console.log('Testing session timeout...');
    this.forceLogoutAndRedirect();
  }

  /**
   * Extend session timeout (useful for long-running operations)
   */
  public extendSession(additionalMinutes: number = 30): void {
    this.lastActivityTime = Date.now() - (this.SESSION_TIMEOUT_MS - (additionalMinutes * 60 * 1000));
    sessionStorage.setItem('lastActivityTime', this.lastActivityTime.toString());
    this.updateTimers();
  }
}
